import { useEffect, useRef, useState } from 'react';
import { WebSocketMessage, ParticipantJoinedEvent, AvailabilityUpdatedEvent, ParticipantLeftEvent } from '../types';

const WS_BASE = import.meta.env.VITE_WS_URL || 'http://localhost:3000';
const WS_PROTOCOL = WS_BASE.replace('http', 'ws').replace('https', 'wss');

interface UseSocketProps {
  roomId: string | null;
  participantName: string | null;
  onParticipantJoined?: (event: ParticipantJoinedEvent) => void;
  onAvailabilityUpdated?: (event: AvailabilityUpdatedEvent) => void;
  onParticipantLeft?: (event: ParticipantLeftEvent) => void;
}

export function useSocket({
  roomId,
  participantName,
  onParticipantJoined,
  onAvailabilityUpdated,
  onParticipantLeft,
}: UseSocketProps) {
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!roomId || !participantName) return;

    const wsUrl = `${WS_PROTOCOL}/ws/${roomId}/${participantName}`;
    console.log('Connecting to WebSocket:', wsUrl);
    const socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      setIsConnected(true);
      sendMessage('join_room', {
        room_id: roomId,
        participant_name: participantName,
      });
    };

    socket.onclose = () => {
      setIsConnected(false);
    };

    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
      setIsConnected(false);
    };

    socket.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        
        switch (message.type) {
          case 'participant_joined':
            onParticipantJoined?.(message.data as ParticipantJoinedEvent);
            break;
          case 'availability_updated':
            onAvailabilityUpdated?.(message.data as AvailabilityUpdatedEvent);
            break;
          case 'participant_left':
            onParticipantLeft?.(message.data as ParticipantLeftEvent);
            break;
          default:
            console.log('Unknown message type:', message.type);
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    socketRef.current = socket;

    return () => {
      if (socket.readyState === WebSocket.OPEN) {
        sendMessage('leave_room', {
          room_id: roomId,
          participant_name: participantName,
        });
      }
      socket.close();
      socketRef.current = null;
    };
  }, [roomId, participantName, onParticipantJoined, onAvailabilityUpdated, onParticipantLeft]);

  const sendMessage = (type: string, data: any) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type, data }));
    }
  };

  const markAvailability = (day: string, hour: number, available: boolean) => {
    if (!roomId || !participantName) return;
    
    sendMessage('mark_availability', {
      room_id: roomId,
      participant: participantName,
      day,
      hour,
      available,
    });
  };

  return {
    isConnected,
    markAvailability,
    sendMessage,
  };
}
